/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** The arithmetic behind the mobile plan carousel. */

/**
 * How far a pointer has to travel before it counts as a swipe.
 *
 * A fraction of the card, not a pixel count: the card is 90% of whatever the
 * phone is, and a threshold in pixels would be a flick on one device and a
 * shove on another.
 */
export const SWIPE_FRACTION = 0.15;

/**
 * Past this, the gesture was a drag and the release is not a click.
 *
 * Fingers and mice both wander a few pixels while pressing. Treating that as
 * a swipe would make every tap scroll, and treating a real drag as a tap
 * would open the checkout the buyer was trying to scroll past.
 */
export const DRAG_SLOP_PX = 8;

/**
 * Which card a free scroll has landed nearest.
 *
 * @param scrollLeft - The track's scroll offset.
 * @param cardWidth - One card plus its gap.
 * @param count - How many cards there are.
 * @returns The index now centred, clamped into range.
 */
export function nearestIndex(
  scrollLeft: number, cardWidth: number, count: number,
): number {
  if (cardWidth <= 0 || count <= 0) return 0;
  return clamp(Math.round(scrollLeft / cardWidth), count);
}

/**
 * Where a pointer drag leaves the carousel.
 *
 * One card per gesture, however far the pointer went. A long drag that jumped
 * three cards would lose the two in between, and the cards are the thing
 * being compared.
 *
 * @param from - The index the drag started on.
 * @param deltaX - Pointer travel; negative moves forward.
 * @param cardWidth - One card plus its gap.
 * @param count - How many cards there are.
 * @returns The index to settle on.
 */
export function indexAfterDrag(
  from: number, deltaX: number, cardWidth: number, count: number,
): number {
  if (cardWidth <= 0 || count <= 0) return 0;
  if (Math.abs(deltaX) < cardWidth * SWIPE_FRACTION) return clamp(from, count);
  return clamp(from + (deltaX < 0 ? 1 : -1), count);
}

/**
 * Whether the release that ends a gesture should also open the card.
 *
 * @param deltaX - How far the pointer travelled before release.
 * @returns Whether to treat the release as a click.
 */
export function isClick(deltaX: number): boolean {
  return Math.abs(deltaX) <= DRAG_SLOP_PX;
}

/** Which arrows have somewhere to go. */
export interface ArrowState {
  back: boolean;
  forward: boolean;
}

/**
 * Whether each arrow leads anywhere from here.
 *
 * Drawn either way, greyed at the ends: an arrow that disappears moves the
 * dots under the reader's thumb between swipes.
 *
 * @param index - The card on screen.
 * @param count - How many cards there are.
 * @returns Which arrows are live.
 */
export function arrowState(index: number, count: number): ArrowState {
  return { back: index > 0, forward: index < count - 1 };
}

/**
 * Clamps an index into the cards that exist.
 *
 * @param index - The candidate.
 * @param count - How many cards there are.
 * @returns The index, inside the range.
 */
function clamp(index: number, count: number): number {
  return Math.max(0, Math.min(count - 1, index));
}
